import { Button, Layout, Space, theme } from "antd";
import { Content, Header } from "antd/es/layout/layout";
import { useEffect, useMemo } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useAppSelector } from "../../../app/store";
import FeedbackIcon from "../../../assets/icons/FeedbackIcon";
import SidebarBashIcon from "../../../assets/icons/SidebarBashIcon";
import useModal from "../../../hooks/useModal";
import { SidebarLayoutProps } from "../../../layouts/SidebarLayout";
import { GetEmployeeDetailsResponseType } from "../../../types";
import { getSanitizedPathname } from "../../../utils/utils";
import AddInfoModalAfterLogin from "../../Auth/AddInfoModalAfterLogin";
import NotificationMenu from "./NotificationMenu";
import ProfileMenu from "./ProfileMenu";

interface SidebarContentProps extends SidebarLayoutProps {
  collapsed?: boolean;
  handleCollapsed: () => void;
  handleShowSidebar: () => void;
  data?: GetEmployeeDetailsResponseType;
  isLoading: boolean;
  contentClass?: string;
}

export interface CheckedData {
  name?: string | null;
  gender?: string | null;
  department_id?: number;
  business_role_id?: number;
  joiningDate?: string | null;
}

export default function SidebarContent({
  handleShowSidebar,
  handleCollapsed,
  children,
  title,
  tabList,
  data,
  isLoading,
  contentClass,
}: SidebarContentProps) {
  const {
    token: { colorBgContainer },
  } = theme.useToken();
  const location = useLocation();
  const params = useParams();
  const { authInfo } = useAppSelector((state) => state.auth);
  const { open, handleClose } = useModal<CheckedData>();

  const userData = useMemo(
    () => ({
      name: data?.data?.employee?.member?.profile?.name,
      gender: data?.data?.employee?.member?.profile?.gender,
      department_id: data?.data?.employee?.role?.department?.id,
      business_role_id: data?.data?.employee?.role?.id,
      joiningDate: data?.data?.employee?.join_date,
    }),
    [data?.data?.employee]
  );

  useEffect(() => {
    const userDataLength = Object.values(userData)?.filter(
      (item) => item
    )?.length;

    if (userDataLength === 5) {
      return handleClose();
    }

    // TODO: Comment for build
    // if (userDataLength !== 5 && !isLoading) {
    //   handleOpen({
    //     type: "UPDATE",
    //     data: userData,
    //   });
    // }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  return (
    <Layout>
      <div>
        <div className="sticky !bg-white z-50 top-0">
          <Header
            style={{
              padding: 0,
              background: colorBgContainer,
            }}
            className="h-20 border-b border-extra-light"
          >
            <div className="flex items-center max-h-full justify-between pr-5 h-full">
              <div className="flex items-center h-full">
                <div className="h-full flex justify-center items-center pl-6 pr-3">
                  <Button
                    type="text"
                    icon={<SidebarBashIcon />}
                    onClick={handleShowSidebar}
                    className="lg:hidden flex justify-center items-center"
                    size="small"
                  />
                  <Button
                    type="text"
                    icon={<SidebarBashIcon />}
                    onClick={handleCollapsed}
                    className="hidden lg:flex justify-center items-center"
                    size="small"
                  />
                </div>
                {title && (
                  <h4 className="text-sub-header text-lg sm:text-[22px] font-semibold leading-[33px] mr-3">
                    {title}
                  </h4>
                )}

                <a
                  href="https://docs.google.com/forms/d/1F5_g0G2LMgU1cb6H1Nh_P-7TsgAZ7-O49vpgoOS4jnM/viewform?edit_requested=true"
                  target="_blank"
                  rel="noreferrer"
                >
                  <Button
                    className="hidden md:flex outline-button-primary"
                    icon={<FeedbackIcon />}
                    size="small"
                  >
                    Feedback
                  </Button>
                </a>
              </div>

              <div className="flex items-center gap-6">
                {authInfo?.role === "Admin" ? <NotificationMenu /> : null}
                <ProfileMenu />
              </div>
            </div>
          </Header>
          {tabList?.length ? (
            <Space
              size={16}
              align="center"
              className="w-full h-16 lg:h-14 px-6 bg-white border-b border-extra-light overflow-x-auto custom-scrollbar"
            >
              {tabList.map((tab) => {
                const currentPath = getSanitizedPathname(
                  location.pathname,
                  params
                );

                return (
                  <Link
                    to={tab?.disabled ? "" : tab?.redirect || tab.link}
                    className={`px-1.5 text-base font-medium inline-block relative after:absolute after:w-full after:left-0 after:-bottom-3 cursor-pointer select-none whitespace-nowrap ${
                      currentPath === tab.link
                        ? "!text-primary after:bg-primary after:h-0.5"
                        : "!text-body-text after:h-0"
                    }`}
                    key={tab.label}
                  >
                    {tab.label}
                  </Link>
                );
              })}
            </Space>
          ) : undefined}
        </div>

        <Content
          className={`p-6 bg-[#f7faff] min-h-[calc(100vh-5rem)] ${contentClass}`}
          // ${
          //   tabList?.length
          //     ? "min-h-[calc(100vh-7.875rem)]"
          //     : "min-h-[calc(100vh-5rem)]"
          // }
        >
          {/* if the login user does not have enough information, then open the modal */}
          {open?.type === "UPDATE" && (
            <AddInfoModalAfterLogin
              open={open.open}
              data={open.data}
              handleClose={handleClose}
              // handleOpen={handleOpen}
            />
          )}

          {children}
        </Content>
      </div>
    </Layout>
  );
}
